'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';

export async function generateCharacterReference(input: {
  bookId: string;
  extractedName: string;
  forceRegenerate?: boolean;
  photoBase64?: string;
  photoConsentVersion?: string;
}): Promise<ActionResult<{ imageUrl: string | null; tokensUsed: number | null }>> {
  try {
    const familyId = await SessionService.getFamilyId();
    const userId = await SessionService.getUserId();
    if (!familyId || !userId) return { success: false, error: 'Not authenticated' };
    // Fail closed: a source photo may depict a child (Art. 9) — require consent.
    if (input.photoBase64 && !input.photoConsentVersion) {
      return { success: false, error: 'PHOTO_CONSENT_REQUIRED' };
    }

    const { CharacterReferenceService } = await import('@/contexts/illustrations/services/CharacterReferenceService');
    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const { getCharacterRepository } = await import('@/contexts/characters/repositories');
    const { getTokenManager } = await import('@/contexts/subscriptions/services/getTokenManager');

    /**
     * An OUTFIT VARIANT («Варя — пижама», wardrobeRefs.ts) is drawn again as an edit of its
     * canonical reference — visibly, billed like a portrait — never as a portrait of its own.
     */
    const book = await getIllustrationRepository().getById(input.bookId, familyId);
    if (book?.characterReferences.some((r) => r.extractedName === input.extractedName && !!r.variantOf)) {
      const { orderWardrobeVariants } = await import('@/contexts/illustrations/services/wardrobeRefs.server');
      const v = await orderWardrobeVariants({ bookId: input.bookId, familyId, redraw: [input.extractedName] });
      if (!v.ordered.length && !v.reused.length && !v.waiting.length) {
        return { success: false, error: 'Character reference generation failed' };
      }
      return { success: true, data: { imageUrl: null, tokensUsed: null } };
    }

    const service = new CharacterReferenceService(
      getIllustrationRepository(),
      getCharacterRepository(),
      getTokenManager(),
    );

    const result = await service.generateReference({
      bookId: input.bookId,
      familyId,
      extractedName: input.extractedName,
      createdBy: userId,
      forceRegenerate: input.forceRegenerate ?? false,
      photoBase64: input.photoBase64,
      photoConsentVersion: input.photoConsentVersion,
    });

    if (!result.success) {
      return { success: false, error: 'Character reference generation failed' };
    }
    return {
      success: true,
      data: { imageUrl: result.imageUrl || null, tokensUsed: result.tokensUsed || null },
    };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('generateCharacterReference', e) };
  }
}
